import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Separator } from '@/components/ui/separator'
import { ChevronDown } from 'lucide-react'
import { FeatureToggle } from '../shared/FeatureToggle'
import type { WidgetConfiguration, WidgetFeatures } from '../../types'

interface FeaturesSectionProps {
  config: WidgetConfiguration
  onUpdate: (updates: Partial<WidgetConfiguration>) => void
  isOpen: boolean
  onToggle: () => void
}

export function FeaturesSection({ config, onUpdate, isOpen, onToggle }: FeaturesSectionProps) {
  const updateFeature = (key: keyof WidgetFeatures, value: boolean) => {
    onUpdate({
      features: {
        ...config.features,
        [key]: value,
      },
    })
  }

  return (
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <CollapsibleTrigger className="flex w-full items-center justify-between rounded-lg border p-4 hover:bg-accent">
        <h3 className="font-semibold">Features</h3>
        <ChevronDown
          className={`h-4 w-4 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-4 space-y-6">
        {/* Notifications */}
        <div className="space-y-3">
          <h4 className="font-medium text-sm">Notifications</h4>
          <div className="space-y-3">
            <FeatureToggle
              id="greeting_overlays"
              label="Greeting Overlays"
              description="Show welcome messages when chat opens"
              checked={config.features.greeting_overlays}
              onChange={(checked) => updateFeature('greeting_overlays', checked)}
            />
            <FeatureToggle
              id="notification_badge"
              label="Notification Badge"
              description="Display unread message count badge"
              checked={config.features.notification_badge}
              onChange={(checked) => updateFeature('notification_badge', checked)}
            />
            <FeatureToggle
              id="tab_title_notifications"
              label="Tab Title Notifications"
              description="Update browser tab title on new messages"
              checked={config.features.tab_title_notifications}
              onChange={(checked) => updateFeature('tab_title_notifications', checked)}
            />
            <FeatureToggle
              id="inactivity_nudge"
              label="Inactivity Nudge"
              description="Prompt users after period of inactivity"
              checked={config.features.inactivity_nudge}
              onChange={(checked) => updateFeature('inactivity_nudge', checked)}
            />
          </div>
        </div>

        <Separator />

        {/* UI Elements */}
        <div className="space-y-3">
          <h4 className="font-medium text-sm">UI Elements</h4>
          <div className="space-y-3">
            <FeatureToggle
              id="expanded_mode"
              label="Expanded Mode"
              description="Allow widget to expand to fullscreen"
              checked={config.features.expanded_mode}
              onChange={(checked) => updateFeature('expanded_mode', checked)}
            />
            <FeatureToggle
              id="timestamps"
              label="Timestamps"
              description="Show message timestamps"
              checked={config.features.timestamps}
              onChange={(checked) => updateFeature('timestamps', checked)}
            />
            <FeatureToggle
              id="scroll_to_bottom_button"
              label="Scroll to Bottom Button"
              description="Show button to jump to latest message"
              checked={config.features.scroll_to_bottom_button}
              onChange={(checked) => updateFeature('scroll_to_bottom_button', checked)}
            />
            <FeatureToggle
              id="new_message_divider"
              label="New Message Divider"
              description="Show divider for new unread messages"
              checked={config.features.new_message_divider}
              onChange={(checked) => updateFeature('new_message_divider', checked)}
            />
          </div>
        </div>

        <Separator />

        {/* Input Options */}
        <div className="space-y-3">
          <h4 className="font-medium text-sm">Input Options</h4>
          <div className="space-y-3">
            <FeatureToggle
              id="input_toolbar"
              label="Input Toolbar"
              description="Show toolbar with formatting options"
              checked={config.features.input_toolbar}
              onChange={(checked) => updateFeature('input_toolbar', checked)}
            />
            <FeatureToggle
              id="attachments"
              label="Attachments"
              description="Allow users to attach files"
              checked={config.features.attachments}
              onChange={(checked) => updateFeature('attachments', checked)}
            />
            <FeatureToggle
              id="emoji_picker"
              label="Emoji Picker"
              description="Show emoji picker in input area"
              checked={config.features.emoji_picker}
              onChange={(checked) => updateFeature('emoji_picker', checked)}
            />
            <FeatureToggle
              id="mic_input"
              label="Voice Input"
              description="Enable microphone for voice messages"
              checked={config.features.mic_input}
              onChange={(checked) => updateFeature('mic_input', checked)}
            />
          </div>
        </div>

        <Separator />

        {/* Audio */}
        <div className="space-y-3">
          <h4 className="font-medium text-sm">Audio</h4>
          <div className="space-y-3">
            <FeatureToggle
              id="sound_effects"
              label="Sound Effects"
              description="Play sounds for new messages and actions"
              checked={config.features.sound_effects}
              onChange={(checked) => updateFeature('sound_effects', checked)}
            />
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
